import React from 'react';
import { NeoInput, MasterSidebar, NeoTabs } from '../../../components/Common';
import { formatDate } from '../../../utils/date';
import { useAuthorization } from '../../../hooks/useAuthorization';
import { getEventColor } from '../../Grade/utils/gridUtils';

const TABS = [
    { id: "technical", label: "Avaliação", icon: "mdi-medal-outline" },
    { id: "performance", label: "Testes", icon: "mdi-timer-outline" },
    { id: "extras", label: "Extras", icon: "mdi-star-outline" },
];

export const EvaluationSidebar = ({
    activeTab,
    setActiveTab,
    selectedEmployeeId,
    setSelectedEmployeeId,
    employees,
    currentDate,
    handlePrevDay,
    handleNextDay,
    todaySchedules,
    selectedSchedule,
    setSelectedSchedule
}) => {
    const { hasFeature } = useAuthorization();
    const canTest = hasFeature('performance_module');
    const canExtra = hasFeature('gamification');

    const handleTabClick = (tabId) => {
        if (tabId === 'performance' && !canTest) {
            alert("Seu plano básico não possui o módulo de Performance e Testes ativado. Faça um upgrade para desbloquear esta aba.");
            return;
        }
        if (tabId === 'extras' && !canExtra) {
            alert("Seu plano básico não possui o módulo de Gamificação e Extras ativado. Faça um upgrade para desbloquear esta aba.");
            return;
        }
        setActiveTab(tabId);
    };

    const tabs = TABS.map(t => {
        const isLocked = (t.id === 'performance' && !canTest) || (t.id === 'extras' && !canExtra);
        return {
            ...t,
            icon: isLocked ? "mdi-lock" : t.icon,
        };
    });

    const headerContent = (
        <div style={{ fontFamily: 'Outfit, sans-serif' }}>
            {/* 1. DATE NAVIGATION */}
            <div className="d-flex align-items-center justify-content-between mb-3 pt-1">
                <button
                    type="button"
                    className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center border border-light-subtle shadow-sm p-0"
                    style={{ width: 32, height: 32 }}
                    onClick={handlePrevDay}
                    disabled={!handlePrevDay}
                >
                    <i className="mdi mdi-chevron-left fs-4" />
                </button>

                <div className="text-center flex-grow-1">
                    <span className="fw-bold text-dark fs-5">{formatDate(currentDate)}</span>
                </div>

                <button
                    type="button"
                    className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center border border-light-subtle shadow-sm p-0"
                    style={{ width: 32, height: 32 }}
                    onClick={handleNextDay}
                    disabled={!handleNextDay}
                >
                    <i className="mdi mdi-chevron-right fs-4" />
                </button>
            </div>

            {/* 2. NEO TABS */}
            <NeoTabs
                activeTab={activeTab}
                onChangeTab={handleTabClick}
                tabs={tabs}
                style={{ marginBottom: "12px", borderBottom: '1.5px solid rgba(72, 39, 18, 0.08)' }}
            />

            {/* 3. INSTRUCTOR FILTER */}
            <div className="mt-1">
                <NeoInput
                    type="select"
                    value={selectedEmployeeId}
                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                    size="sm"
                    icon="mdi-account-search-outline"
                >
                    <option value="">Todos os Professores</option>
                    {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>
                            {emp.name || `${emp.firstName || ""} ${emp.lastName || ""}`.trim()}
                        </option>
                    ))}
                </NeoInput>
            </div>
        </div>
    );

    const renderScheduleItem = (schedule, isActive) => {
        const startTime = schedule?.startTime || "";
        const endTime = schedule?.endTime || "";
        const activityName = schedule?.activityName || "Atividade";
        const teacherName = schedule?.employeeName ? `Prof. ${schedule.employeeName.split(" ")[0]}` : "";
        const areaName = schedule?.areaName || "";
        const color = getEventColor(schedule) || "var(--color-brand-accent)";

        return (
            <div className="d-flex align-items-center w-100 overflow-hidden" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {/* Faixa vertical colorida da atividade */}
                <div 
                    className="flex-shrink-0 me-3" 
                    style={{ 
                        width: '4px', 
                        height: '32px', 
                        borderRadius: '2px', 
                        backgroundColor: color 
                    }} 
                />

                <div className="text-start flex-grow-1 overflow-hidden">
                    <div className="d-flex align-items-center justify-content-between gap-2">
                        <p className={`mb-0 fw-bold text-truncate ${isActive ? 'text-primary' : 'text-dark'}`} style={{ fontSize: '13px' }}>
                            {activityName}
                        </p>
                        {schedule?.enrolledCount !== undefined && (
                            <span className="badge bg-light text-muted border font-size-10 px-2 py-0.5 rounded-pill flex-shrink-0">
                                {schedule.enrolledCount}/{schedule.maxCapacity || "—"}
                            </span>
                        )}
                    </div>
                    <div className="d-flex flex-wrap gap-2 align-items-center mt-1 text-muted small" style={{ fontSize: '11px', opacity: 0.8 }}>
                        <span className="d-flex align-items-center">
                            <i className="mdi mdi-clock-outline me-1"></i>
                            {startTime}–{endTime}
                        </span>
                        {teacherName && (
                            <span className="d-flex align-items-center">
                                <i className="mdi mdi-account-star-outline me-1"></i>
                                {teacherName}
                            </span>
                        )}
                        {areaName && (
                            <span className="d-flex align-items-center">
                                <i className="mdi mdi-map-marker-outline me-1"></i>
                                {areaName}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={selectedSchedule ? "d-none d-md-block" : "d-block"}>
            <MasterSidebar
                title="Turmas"
                subtitle="Aulas e horários agendados"
                items={todaySchedules || []}
                activeId={selectedSchedule?.id}
                onSelect={(schedule) => setSelectedSchedule(schedule)}
                loading={false}
                headerContent={headerContent}
                renderItem={renderScheduleItem}
                hideSearch={true}
                icon="mdi mdi-calendar-clock"
            />
        </div>
    );
};
